import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Group, Card, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TokenText } from '@repletoryreach/sdk-ui/inputs';
import type { broadcasts, templates as templatesApi, channelWa } from '@repletoryreach/sdk-api';
import { useCredits } from '../../../../lib/credits';
import { formatCount } from '../../../../lib/format';
import { describeInstant, scheduledInstant } from '../draft';
import { useTemplateLanguageName } from '../languages';
import type { StepProps } from './shared';

/**
 * Step 4: review and send.
 *
 * **This is the confirmation.** There is deliberately no "are you sure" dialog
 * on top of it. A modal that repeats what the page already says teaches people
 * to click through both, and the figures that matter (how many, how much, what
 * is left) do not fit in one anyway. So they are here, large, and Send is the
 * only button that sends.
 *
 * Every number comes from `POST /v1/broadcasts/preview`, which resolves the
 * audience with the create route's own code and asks billing the question the
 * start route asks. When the wallet cannot cover it, this step refuses with the
 * server's own refusal and its own figures, and offers the top-up page: nobody
 * gets to press Send and be turned away afterwards.
 */
export function ReviewStep({
  state, t, timezone, template, perLanguage = false, isSms, isEmail = false, preview, previewing, audienceLabel, number
}: StepProps & {
  timezone: string;
  template: templatesApi.MessageTemplate | null;
  /** Each person is sent the version in their language: the counts per version are shown. */
  perLanguage?: boolean;
  /** An SMS broadcast: no number, no template, the text and its parts instead. */
  isSms: boolean;
  /** An email broadcast (4 October 2026): no number, no template; the subject, the kind and one email each. */
  isEmail?: boolean;
  preview: broadcasts.BroadcastPreview | null;
  previewing: boolean;
  audienceLabel: string;
  number: channelWa.PhoneNumber | null;
}) {
  const credits = useCredits();
  const at = state.sendMode === 'schedule' ? scheduledInstant(state.sendAt, timezone) : null;
  const estimate = preview?.estimate ?? null;
  const refusal = preview?.refusal ?? null;
  const short = refusal?.code === 'INSUFFICIENT_CREDITS';
  const parts = isSms ? preview?.sms_segments ?? null : null;
  const languageName = useTemplateLanguageName();
  /*
   * How many are sent each version ("English (US): 120, Afrikaans: 30"), counted by the server over the real roll
   * with the code the send uses. Nothing is said about price here: it is per message, whatever the language.
   */
  const languageCounts = perLanguage && preview?.languages?.length
    ? preview.languages
      .map((l) => t('broadcasts.new.review.languageCount', { language: languageName(l.language), count: formatCount(l.count) }))
      .join(', ')
    : null;

  return (
    <Stack gap="lg">
      <div className="rr-bc-new__review">
        <Card withBorder className="rr-bc-new__figure">
          <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.new.review.recipients')}</Text>
          <Text role="headline">{formatCount(preview?.reachable ?? 0)}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('broadcasts.new.review.recipientsHint', {
              matched: formatCount(preview?.matched ?? 0),
              excluded: formatCount(preview?.opted_out ?? 0)
            })}
          </Text>
        </Card>

        <Card withBorder className="rr-bc-new__figure">
          <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.new.review.cost')}</Text>
          <Text role="headline">
            {estimate ? credits(estimate.total_minor, estimate.currency) : previewing ? '…' : '–'}
          </Text>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {estimate
              ? parts
                ? t(parts === 1 ? 'broadcasts.sms.review.costHint.one' : 'broadcasts.sms.review.costHint.other', {
                  count: formatCount(estimate.recipients), parts
                })
                : isEmail
                  ? t('broadcasts.email.review.costHint', { count: formatCount(estimate.recipients) })
                  : t('broadcasts.new.review.costHint', { count: formatCount(estimate.recipients) })
              : t('broadcasts.new.review.costUnknown')}
          </Text>
        </Card>

        <Card withBorder className="rr-bc-new__figure">
          <Text role="caption" c="var(--rr-color-text-muted)">{t('broadcasts.new.review.balance')}</Text>
          <Text role="headline">
            {estimate ? credits(estimate.balance_minor, estimate.currency) : '–'}
          </Text>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {estimate
              ? t('broadcasts.new.review.balanceHint', {
                after: credits(Math.max(0, estimate.balance_minor - estimate.total_minor), estimate.currency)
              })
              : t('broadcasts.new.review.balanceUnknown')}
          </Text>
        </Card>
      </div>

      <Card withBorder>
        <Stack gap={8}>
          <Text role="bodySmall" fw={600}>{t('broadcasts.form.summary')}</Text>
          {isEmail ? (
            <Line
              label={t('broadcasts.sms.channel.label')}
              value={t('broadcasts.email.channel')}
              badge={state.emailKind === 'service' ? t('broadcasts.sms.kind.service') : t('broadcasts.sms.kind.marketing')}
            />
          ) : isSms ? (
            <Line
              label={t('broadcasts.sms.channel.label')}
              value={t('broadcasts.sms.channel.sms')}
              badge={state.smsKind === 'service' ? t('broadcasts.sms.kind.service') : t('broadcasts.sms.kind.marketing')}
            />
          ) : (
            <Line label={t('broadcasts.form.phoneNumber')} value={number?.msisdn ?? number?.display_name ?? '–'} />
          )}
          <Line label={t('broadcasts.form.audience')} value={audienceLabel || '–'} />
          {isEmail ? (
            <>
              <Line
                label={t('broadcasts.email.subject.label')}
                value={<span className="rr-bc-new__sms-text"><TokenText value={state.emailSubject.trim()} /></span>}
              />
              {state.emailButtonLabel.trim() && state.emailButtonUrl.trim() && (
                <Line
                  label={t('broadcasts.email.button.label')}
                  value={<span className="rr-bc-new__sms-text">{`${state.emailButtonLabel.trim()} (${state.emailButtonUrl.trim()})`}</span>}
                />
              )}
              <Line
                label={t('broadcasts.email.review.perEmail')}
                value={estimate && estimate.recipients > 0
                  ? credits(Math.round(estimate.total_minor / estimate.recipients), estimate.currency)
                  : '–'}
              />
            </>
          ) : isSms ? (
            <>
              <Line
                label={t('broadcasts.sms.body.label')}
                value={<span className="rr-bc-new__sms-text"><TokenText value={state.smsBody.trim()} /></span>}
              />
              <Line label={t('broadcasts.sms.review.parts')} value={parts !== null ? String(parts) : '–'} />
            </>
          ) : (
            <Line
              label={t('broadcasts.columns.template')}
              value={template ? `${template.code} (${template.language})` : '–'}
              badge={template?.category ? t(`broadcasts.category.${template.category}`, template.category) : undefined}
            />
          )}
          {!isSms && !isEmail && perLanguage && (
            <Line
              label={t('broadcasts.new.review.languages')}
              value={languageCounts ?? (previewing ? '…' : '–')}
            />
          )}
          <Line
            label={t('broadcasts.form.sendAt')}
            value={at
              ? t('broadcasts.new.review.at', { when: describeInstant(at, timezone), zone: timezone })
              : t('broadcasts.new.review.immediately')}
          />
          <Line label={t('broadcasts.form.throttle')} value={String(state.throttle)} />
          {/* Each placeholder's value, with a contact field painted as the
              chip it was picked as, so what will be resolved per person is
              told apart from words everybody gets. */}
          {!isSms && !isEmail && Object.entries(state.variables)
            .filter(([, v]) => v.trim())
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([pos, v]) => (
              <Line
                key={pos}
                label={t('broadcasts.new.review.placeholder', { index: pos })}
                value={<TokenText value={v} />}
              />
            ))}
        </Stack>
      </Card>

      {/*
       * What happens if the money runs out part-way, said before it can.
       *
       * The runner checks credit per message, so a wallet that empties mid-send
       * stops that broadcast where it is: the people already reached stay
       * reached and are charged, the rest are left queued and nothing further
       * goes out. Worth saying plainly, because "it stops" is not what most
       * people assume happens.
       */}
      {!short && estimate && (
        <Alert color="info" title={t('broadcasts.new.review.runsOut.title')}>
          {t('broadcasts.new.review.runsOut.body')}
        </Alert>
      )}

      {short && refusal && (
        <Alert color="error" title={t('broadcasts.form.estimate.short.title')} role="alert">
          <Stack gap={8}>
            {/* The server's refusal, with the server's own figures. The same
                sentence the start route's 402 produces, so a person cannot be
                told two different things about one shortfall. */}
            <Text role="bodySmall">
              {t('broadcasts.start.insufficient', {
                estimate: credits(refusal.estimated_minor ?? 0, refusal.currency),
                balance: credits(refusal.available_minor ?? 0, refusal.currency),
                shortfall: credits(refusal.shortfall_minor ?? 0, refusal.currency),
                count: formatCount(refusal.recipients ?? 0)
              })}
            </Text>
            <Link to="/billing/topup" className="rr-bc-new__link">{t('broadcasts.new.review.topUp')}</Link>
          </Stack>
        </Alert>
      )}

      {refusal && !short && (
        <Alert color="error" role="alert">
          {t(`broadcasts.new.refusal.${refusal.code}`, refusal.message)}
        </Alert>
      )}

      {!preview && !previewing && (
        <Alert color="warning">{t('broadcasts.new.review.noPreview')}</Alert>
      )}
    </Stack>
  );
}

function Line({ label, value, badge }: { label: string; value: ReactNode; badge?: string }) {
  return (
    <Group justify="space-between" gap="sm" className="rr-bc-new__line">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Group gap={6}>
        <Text role="bodySmall" fw={500}>{value}</Text>
        {badge && <Badge size="xs" variant="light">{badge}</Badge>}
      </Group>
    </Group>
  );
}
